import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { AnalyticsProvider } from '@treams/treams-analytics';
import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import { useAnalyticsInterval } from '@treams/treams-hooks/src/useAnalyticsInterval';
import * as graphql from '@treams/treams-network';
import { GroupType } from '@treams/treams-network';
import { Button, type ITimeMachine, createTimeMachineFromQuery } from '@treams/treams-ui';

import { useGroupSelection } from '../goals/group-selector/new/useGroupSelection';
import { AnalyticsDownloads } from './AnalyticsDownloads';
import {
	ANALYTICS_VIEW_KEY,
	ANALYTICS_VIEW_VALUE_OTHER,
	AnalyticsHeaderTabs,
	AnalyticsRoutes,
} from './AnalyticsRoutes';
import { AnalyticsView } from './AnalyticsView';

export function Analytics() {
	const roles = useViewerRoles();

	const location = useLocation();
	const history = useHistory();
	const params = new URLSearchParams(location.search);
	const viewParam = params.get(ANALYTICS_VIEW_KEY);

	const hasCycleAnalytics = useFeature(graphql.FeatureName.CycleAnalytics);
	const hasTalentReviewAnalytics = useFeature(graphql.FeatureName.TalentReviewAnalytics);
	const hasTreamsScore = useFeature(graphql.FeatureName.TreamsScore);
	const hasRewards = useFeature(graphql.FeatureName.Rewards);
	const showEngagementAnalytics =
		roles.isEngagementOfficer && viewParam !== ANALYTICS_VIEW_VALUE_OTHER;
	const hasTeamAnalytics = useFeature(graphql.FeatureName.AnalyticsTeams);

	const selectableGroupsFilter = useMemo(() => {
		return (group: graphql.Group) => {
			if (showEngagementAnalytics) {
				return true;
			}

			const canViewTeam = hasTeamAnalytics && group.type === GroupType.Team;
			const isDepartment = group.type === graphql.GroupType.Department;

			return group.viewerCanViewAnalytics && (canViewTeam || isDepartment);
		};
	}, [hasTeamAnalytics, showEngagementAnalytics]);

	const userCanAccessAllGroups = roles.isWorkspaceSuperuser || showEngagementAnalytics;

	const groupSelection = useGroupSelection({
		canSelect: selectableGroupsFilter,
		setPermissionBasedDefault: !userCanAccessAllGroups,
		localStorageKey: showEngagementAnalytics
			? 'analyticsEngagementOfficerGroupIDs'
			: 'analyticsGroupIDs',
	});

	const queriedTimeMachine = createTimeMachineFromQuery();
	const [timeMachine, setTimeMachine] = useState<ITimeMachine>(queriedTimeMachine);
	const skipInitialTimePopRef = useRef(true);
	const { setInterval, setStartDate, setEndDate } = useAnalyticsInterval();

	useEffect(() => {
		if (skipInitialTimePopRef.current) {
			skipInitialTimePopRef.current = false;
			return;
		}

		if (history.action !== 'POP') {
			return;
		}

		if (
			timeMachine.interval === queriedTimeMachine.interval &&
			timeMachine.urlSafeCurrent[0] === queriedTimeMachine.urlSafeCurrent[0] &&
			timeMachine.urlSafeCurrent[1] === queriedTimeMachine.urlSafeCurrent[1]
		) {
			return;
		}

		setTimeMachine(queriedTimeMachine);
		// queriedTimeMachine is a new object every render; location.search is the POP signal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search, history]);

	useEffect(() => {
		setInterval(timeMachine.interval);
		setStartDate(timeMachine.current[0]);
		setEndDate(timeMachine.current[1]);
	}, [timeMachine, setInterval, setStartDate, setEndDate]);

	return (
		<AnalyticsProvider>
			<AnalyticsRoutes
				hasCycleAnalytics={hasCycleAnalytics}
				hasTalentReviewAnalytics={hasTalentReviewAnalytics}
				hasTreamsScore={hasTreamsScore}
				hasRewards={hasRewards}
				isEngagementOfficer={roles.isEngagementOfficer}
				showEngagementAnalytics={showEngagementAnalytics}
				selectableGroupsFilter={selectableGroupsFilter}
				groupSelection={groupSelection}
				timeMachine={timeMachine}
				onChangeTimeMachine={setTimeMachine}
				overview={
					<AnalyticsView
						groupSelectionFilter={selectableGroupsFilter}
						timeMachine={timeMachine}
						onChangeTimeMachine={setTimeMachine}
						groupSelection={groupSelection}
						engagementOfficerMetricsOnly={showEngagementAnalytics}
						loading={groupSelection.loading}
						headerButton={
							roles.isWorkspaceSuperuser &&
							(groupSelection.loading ? (
								<Button.Skeleton />
							) : (
								<AnalyticsDownloads
									filters={{
										startDate: timeMachine.current[0],
										endDate: timeMachine.current[1],
										groups: groupSelection.groupsInSelection.map((sg) =>
											_.pick(sg, ['id', 'type']),
										),
									}}
								/>
							))
						}
						headerFooter={
							<AnalyticsHeaderTabs
								isInEngagementAnalytics={showEngagementAnalytics}
								isEngagementOfficer={roles.isEngagementOfficer}
								hasCycleAnalytics={hasCycleAnalytics}
								hasTalentReviewAnalytics={hasTalentReviewAnalytics}
								hasTreamsScore={hasTreamsScore}
								hasRewards={hasRewards}
							/>
						}
					/>
				}
			/>
		</AnalyticsProvider>
	);
}
